import { useEffect, useMemo, useState } from 'react';
import {
  ModelDropdownItem,
  ModelDropdownMenuShell,
  ModelDropdownSectionHeader,
  ModelDropdownSeparator,
} from '../common/ModelDropdownMenu';
import { getVideoCreditRatePerSecond } from './VideoGenerationSettings';
import {
  PROVIDER_META,
  videoModelButtonLabel,
  videoModelGroupLabel,
  videoProviderRank,
} from '../common/VideoModelFamilySelector';
import { formatVideoResolutionLabel, videoResolutionSortValue } from '../state/useGenerationState';
import type { GenerationModel } from '../../../features/generation-models/api';
import type { CreditRatesResponse } from '../../../features/organizations/types';
import type { VideoSettings } from './VideoGenerationSettings';
import type { VideoGenerationMode } from '../state/useGenerationState';
import type { UsePortalDropdownReturn } from './hooks/usePortalDropdown';

interface SceneVideoSettings {
  duration: number;
  generateAudio: boolean;
  videoModel?: string;
  resolution?: string;
}

interface VideoModelDropdownPortalProps {
  dropdown: UsePortalDropdownReturn;
  sceneKey: string | null;
  videoGenerationModels: GenerationModel[];
  videoSettings: VideoSettings;
  // モデル選択を「次回もこれを使う」既定として localStorage に永続化するため、
  // シーン単位の override (updateSceneVideoSettings) に加えてシナリオ共通設定も更新する。
  setVideoSettings: React.Dispatch<React.SetStateAction<VideoSettings>>;
  videoGenerationMode: VideoGenerationMode;
  getSceneVideoSettings: (sceneKey: string) => SceneVideoSettings;
  updateSceneVideoSettings: (sceneKey: string, settings: Partial<SceneVideoSettings>) => void;
  creditRatesData: CreditRatesResponse | undefined;
}

// グループ見出し・項目表記・アイコンはシナリオ設定画面の VideoModelFamilySelector と
// 同じヘルパー (metadata.ui_group_label / ui_series_label 正本) を共有する。

/** 解像度フィルタのチップとして扱える表記か (ピクセル解像度 '720' と 'K' 単位表記 '2K')。
 *  数字のみの正規表現だと 'K' 単位表記が丸ごと弾かれ、そのモデルが一切出せなくなる。 */
const isResolutionToken = (r: string) => /^\d+$/.test(r) || /^\d+k$/i.test(r);

/** チップに畳み込む解像度のエイリアス。MiniMax H3 の '768'/'2K' は他モデルと同じ
 *  ピクセル刻み ('720'/'1080') を持たないため、そのままだと自分専用のチップができてしまう。
 *  相当する画質のチップ (768→720p / 2K→1080p) の中に並べる
 *  (生成・課金に使う解像度は '768'/'2K' のまま)。
 *  Gemini Omni スピードの '360' も同様に、単独の 360p チップは出さず 480p チップに
 *  統合する。チップより低い解像度に畳んだ項目は、項目名に実解像度を添えて示す
 *  (下の resolutionSuffixLabel を参照)。 */
const RESOLUTION_CHIP_ALIAS: Record<string, string> = { '360': '480', '768': '720', '2k': '1080' };
const resolutionChipValue = (r: string) => RESOLUTION_CHIP_ALIAS[r.toLowerCase()] ?? r;

/** ドロップダウン項目名。チップより低い解像度をエイリアスで畳み込んだモデル
 *  (例: 480p チップ配下の Omni スピード = 実際は 360p) は、チップの表記どおりの画質だと
 *  誤認させないよう「Omni スピード (360p)」のように実解像度を添える。
 *  チップ以上の解像度 (720p チップ配下の '768' / 1080p チップ配下の '2K') は添えない。 */
function resolutionSuffixLabel(model: GenerationModel, res: string, resolutionFilter: string): string {
  const base = videoModelButtonLabel(model);
  if (videoResolutionSortValue(res) < videoResolutionSortValue(resolutionFilter)) {
    return `${base} (${formatVideoResolutionLabel(res)})`;
  }
  return base;
}

/** チップ (resolutionFilter) に対して、そのモデルが実際に使う解像度を返す。
 *  完全一致を優先し、無ければエイリアス (720p チップ ← '768' / 1080p チップ ← '2K') で解決する。
 *  どちらにも該当しなければ undefined = そのチップでは非表示。 */
function resolveModelResolution(
  model: GenerationModel,
  resolutionFilter: string,
): string | undefined {
  const list = ((model.supported_resolutions ?? ['720', '1080']) as string[]).filter(isResolutionToken);
  if (list.includes(resolutionFilter)) return resolutionFilter;
  return list.find((r) => resolutionChipValue(r) === resolutionFilter);
}

export function VideoModelDropdownPortal({
  dropdown,
  sceneKey,
  videoGenerationModels,
  videoSettings,
  setVideoSettings,
  videoGenerationMode,
  getSceneVideoSettings,
  updateSceneVideoSettings,
  creditRatesData,
}: VideoModelDropdownPortalProps) {
  const scenarioDefaultRes = resolutionChipValue(videoSettings.resolution || '1080');
  const [resolutionFilter, setResolutionFilter] = useState<string>(scenarioDefaultRes);

  // ドロップダウンを開くたびに「現在の実効解像度 (シーン上書き優先)」へリセットする。
  // シナリオ標準に固定すると、シーン側で別解像度を選んでいる場合に選択中モデルが
  // ハイライトされないタブで開いてしまい、現在の選択が視認できない。
  // getSceneVideoSettings は毎レンダー再生成されるため依存には入れず、導出値に依存する
  // (関数 identity を依存にすると、開いている間の再レンダーで手動フィルタ操作が巻き戻る)。
  // 現在の解像度が '768'/'2K' の場合はチップ側の代表値 ('720'/'1080') へ畳んでからリセットする。
  const effectiveCurrentRes = resolutionChipValue(
    (sceneKey ? getSceneVideoSettings(sceneKey).resolution : undefined)
    || videoSettings.resolution
    || '1080',
  );
  useEffect(() => {
    if (dropdown.openId) {
      setResolutionFilter(effectiveCurrentRes);
    }
  }, [dropdown.openId, effectiveCurrentRes]);

  // 全モデルから選択可能な解像度の和集合を求める (降順)。
  // ピクセル解像度 ('720'/'1080') に加え、MiniMax 等の 'K' 単位表記 ('2K'/'4K') も対象にする。
  // '768'/'2K' は RESOLUTION_CHIP_ALIAS で 720p/1080p チップに畳み込むため単独チップにはしない。
  const availableResolutions = useMemo(() => {
    const set = new Set<string>();
    for (const m of videoGenerationModels) {
      const res = (m.supported_resolutions ?? ['720', '1080']) as string[];
      for (const r of res) {
        if (isResolutionToken(r)) set.add(resolutionChipValue(r));
      }
    }
    return Array.from(set).sort((a, b) => videoResolutionSortValue(b) - videoResolutionSortValue(a));
  }, [videoGenerationModels]);

  if (!dropdown.openId || !dropdown.pos) return null;

  const scSettings = sceneKey ? getSceneVideoSettings(sceneKey) : null;
  const currentModel = scSettings?.videoModel || videoSettings.videoModel;
  const currentRes = scSettings?.resolution || videoSettings.resolution || '1080';
  const isRefMode = videoGenerationMode === 'reference';
  const isStoryboardMode = videoGenerationMode === 'storyboard';
  // ストーリーボード / 参考画像モードはどちらも reference-to-video を使うため、
  // r2v / mmref をサポートしないモデル (Veo Lite 等) は利用不可。
  const requiresReferenceSupport = isRefMode || isStoryboardMode;
  const audio = scSettings?.generateAudio || false;

  // ストーリーボードモードでも、シナリオ設定で選んだファミリーに縛らず全ファミリーから
  // 動画モデルを選べるようにする (例: Seedance で絵コンテ生成 → Veo で動画生成、逆も可)。
  // reference-to-video 非対応モデルは下の supportsRef フィルタで引き続き除外する。
  const visibleModels = videoGenerationModels;

  // グループ (metadata.ui_group_label) 別に分類して、フレーム側ドロップダウンと同じく
  // セクションヘッダー + グループ間セパレータでグルーピング表示する
  const grouped = new Map<string, typeof videoGenerationModels>();
  for (const m of visibleModels) {
    const key = videoModelGroupLabel(m);
    const arr = grouped.get(key) ?? [];
    arr.push(m);
    grouped.set(key, arr);
  }
  const groupRank = (key: string) => {
    const list = grouped.get(key) ?? [];
    return Math.min(...list.map((m) => videoProviderRank(m.provider)));
  };
  const groupMinSort = (key: string) => {
    const list = grouped.get(key) ?? [];
    return Math.min(...list.map((m) => m.sort_order));
  };
  const orderedGroupKeys: string[] = Array.from(grouped.keys()).sort((a, b) => {
    const rankDiff = groupRank(a) - groupRank(b);
    if (rankDiff !== 0) return rankDiff;
    return groupMinSort(a) - groupMinSort(b);
  });

  const items: React.ReactNode[] = [];
  let renderedGroupCount = 0;
  orderedGroupKeys.forEach((groupKey) => {
    const sorted = [...(grouped.get(groupKey) ?? [])].sort((a, b) => a.sort_order - b.sort_order);

    // フィルタ解像度を実際にサポートするモデルのみ表示する。
    // (例: 480p 非対応の Veo は 480p チップには出さない)
    // MiniMax の '768'/'2K' は 720p/1080p チップ、Omni の '360' は 480p チップで拾い、
    // 生成/課金には元の値をそのまま使う。
    const filteredModels = sorted
      .map((m) => ({ model: m, res: resolveModelResolution(m, resolutionFilter) }))
      .filter((e): e is { model: GenerationModel; res: string } => e.res !== undefined);
    if (filteredModels.length === 0) return;

    if (renderedGroupCount > 0) {
      items.push(<ModelDropdownSeparator key={`sep-${groupKey}`} />);
    }
    renderedGroupCount += 1;

    // アイコンはグループ先頭モデルの provider のものを使う
    items.push(
      <ModelDropdownSectionHeader
        key={`hdr-${groupKey}`}
        label={groupKey}
        iconMask={PROVIDER_META[filteredModels[0]?.model.provider ?? '']?.iconMask}
      />,
    );

    filteredModels.forEach(({ model, res }) => {
      // 参考画像 / ストーリーボードモードでサポート外のモデルは利用不可
      const supportsRef = (model.supported_modes ?? []).includes('r2v')
        || (model.supported_modes ?? []).includes('mmref');
      // ストーリーボードモードは「そもそも非表示」、参考画像モードは「グレーアウト」
      // で告知（参考画像モードは選択中フォールバックの説明として disabled item を残す）。
      if (isStoryboardMode && !supportsRef) {
        return;
      }
      const isModelDisabled = requiresReferenceSupport && !supportsRef;
      if (isModelDisabled) {
        items.push(
          <div
            key={`disabled-note-${model.model_code}`}
            style={{ padding: '4px 12px 2px', fontSize: '11px', color: 'rgba(245, 241, 230, 0.4)' }}
          >
            ※ 「AIおまかせ」では利用できません
          </div>,
        );
      }

      // audio_default_on モデル (Seedance) は音声常時オンとして料金計算
      const audioDefaultOn = (model.metadata as { audio_default_on?: boolean } | null | undefined)?.audio_default_on === true;
      const audioForRate = audioDefaultOn ? true : audio;

      const duration = scSettings?.duration || 4;

      // res は filteredModels で解決済み (チップと同値、またはエイリアス配下の '360'/'768'/'2K')
      const rate = getVideoCreditRatePerSecond(creditRatesData?.rates, model.model_code, res, audioForRate);
      const pts = rate > 0 ? Math.floor(duration * rate) : 0;
      const isActive = currentModel === model.model_code && currentRes === res;
      items.push(
        <ModelDropdownItem
          key={`${model.model_code}-${res}`}
          label={resolutionSuffixLabel(model, res, resolutionFilter)}
          pts={pts > 0 ? `${pts}pt` : undefined}
          active={isActive}
          disabled={isModelDisabled}
          onSelect={() => {
            if (sceneKey) {
              updateSceneVideoSettings(sceneKey, { videoModel: model.model_code, resolution: res });
            }
            // シナリオ共通設定にも反映し、useGenerationState の localStorage 同期 (`videoSettings`)
            // 経由で次回起動時にも同じモデル/解像度を既定として復元できるようにする。
            // ストーリーボードモードはシーン単位の override が事実上 1 つしかなく、
            // リロードで sceneVideoSettings が消えると選択が戻ってしまっていた。
            setVideoSettings((prev) => {
              if (prev.videoModel === model.model_code && prev.resolution === res) return prev;
              return { ...prev, videoModel: model.model_code as typeof prev.videoModel, resolution: res };
            });
            dropdown.close();
          }}
        />,
      );
    });
  });

  return (
    <ModelDropdownMenuShell
      menuRef={dropdown.menuRef}
      pos={dropdown.pos}
      filter={availableResolutions.length > 1 ? {
        label: '解像度',
        options: availableResolutions.map((r) => ({ value: r, label: formatVideoResolutionLabel(r) })),
        value: resolutionFilter,
        onChange: setResolutionFilter,
      } : undefined}
      emptyText="この解像度に対応したモデルがありません"
    >
      {items}
    </ModelDropdownMenuShell>
  );
}
